import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Server, 
  Shield, 
  Gamepad2, 
  Activity, 
  Zap,
  ChevronRight,
  Radio
} from 'lucide-react';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import StatusCard from '../components/StatusCard';
import Loader from '../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Shield,
    title: 'Roleplay Immersif',
    description: 'Une expérience RP authentique avec des règles strictes pour garantir une immersion totale.'
  },
  {
    icon: Users,
    title: 'Communauté Active',
    description: 'Rejoignez une communauté passionnée et bienveillante de joueurs RP.'
  },
  {
    icon: Zap,
    title: 'Scripts Exclusifs',
    description: 'Des fonctionnalités uniques développées spécialement pour notre serveur.'
  },
  {
    icon: Activity,
    title: 'Événements Réguliers',
    description: 'Des événements RP organisés chaque semaine pour animer le serveur.'
  }
];

const Home = () => {
  const { settings, loading: settingsLoading } = useSettings();
  const [serverStatus, setServerStatus] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/server-status`);
        setServerStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch server status:', error);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  if (settingsLoading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1584905399334-110483dd2627?q=85&w=1920&auto=format&fit=crop)'
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent opacity-80" />
        
        {/* Noise Texture */}
        <div className="absolute inset-0 noise-texture" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Server Status Badge */}
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-8">
              <div className={`w-2 h-2 rounded-full ${serverStatus?.online ? 'bg-green-400 pulse-online' : 'bg-red-400'}`} />
              <span className="text-sm text-gray-300">
                {serverStatus?.online ? (
                  <>Serveur en ligne • <span className="text-white font-semibold">{serverStatus.players}</span> joueurs</>
                ) : (
                  'Serveur hors ligne'
                )}
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight mb-6">
              <span className="text-white">Bienvenue sur</span>
              <br />
              <span className="gradient-text neon-text">{settings.server_name}</span>
            </h1>

            <p className="text-gray-300 text-lg sm:text-xl max-w-2xl mx-auto mb-10">
              {settings.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`https://${settings.server_ip}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider px-8 py-4 btn-skew transition-all duration-300 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]"
                data-testid="hero-join-btn"
              >
                <span className="flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5" />
                  Rejoindre le serveur
                </span>
              </a>
              
              <a
                href={settings.discord_link}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 bg-transparent border border-white/20 hover:border-violet-500 text-white font-heading font-bold uppercase tracking-wider px-8 py-4 btn-skew transition-all duration-300"
                data-testid="hero-discord-btn"
              >
                <span className="flex items-center gap-2">
                  Discord
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-violet-500 rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Server Status Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 -mt-32 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatusCard
              title="Statut"
              value={serverStatus?.online ? 'EN LIGNE' : 'HORS LIGNE'}
              icon={Server}
              status={serverStatus?.online ? 'online' : 'offline'}
            />
            <StatusCard
              title="Joueurs"
              value={`${serverStatus?.players || 0}/${serverStatus?.max_players || 100}`}
              subtitle="connectés"
              icon={Users}
              status={serverStatus?.online ? 'online' : 'offline'}
            />
            <StatusCard
              title="Ping"
              value={`${serverStatus?.ping || 0}ms`}
              icon={Activity}
              status={serverStatus?.ping > 100 ? 'warning' : 'default'}
            />
            <StatusCard
              title="Uptime"
              value={`${serverStatus?.uptime_percentage || 100}%`}
              icon={Radio}
              status="default"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-wide mb-4">
              <span className="gradient-text">Pourquoi nous rejoindre ?</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Découvrez ce qui fait de {settings.server_name} une expérience unique
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl border border-white/10 bg-black/40 backdrop-blur-md p-6 hover:border-violet-500/50 transition-all duration-300 card-hover"
              >
                {/* Icon */}
                <div className="w-12 h-12 rounded-lg bg-violet-600/20 flex items-center justify-center mb-4 group-hover:bg-violet-600/30 transition-colors">
                  <feature.icon className="w-6 h-6 text-violet-400" />
                </div>
                
                <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-900/20 to-blue-900/20" />
        <div className="absolute inset-0 noise-texture" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wide mb-6">
              <span className="text-white">Prêt à commencer</span>
              <br />
              <span className="gradient-text">votre aventure ?</span>
            </h2>
            <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
              Rejoignez {settings.server_name} dès maintenant et vivez une expérience roleplay inoubliable.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/join"
                className="group flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider px-8 py-4 btn-skew transition-all duration-300 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]"
                data-testid="cta-join-btn"
              >
                <span className="flex items-center gap-2">
                  Comment rejoindre ?
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              
              <Link
                to="/status"
                className="flex items-center gap-2 text-gray-400 hover:text-white font-heading uppercase tracking-wider px-6 py-4 transition-colors"
                data-testid="cta-status-btn"
              >
                <Activity className="w-5 h-5" />
                Voir le statut du serveur
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
